import React, { useState, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { motion, AnimatePresence } from 'framer-motion';

/* Common */
import { ScrollAnchor } from '~/components/common';

/* Components */
import ConversionFlowDataLayer from './ConversionFlowDataLayer';
import ConversionFlowPrevious from './ConversionFlowPrevious';
import ConversionFlowSteps from './ConversionFlowSteps';
import ConversionFlowSubmit from './ConversionFlowSubmit';
import ConversionFlowSummary from './ConversionFlowSummary';
import ConversionFlowVehicle from './ConversionFlowVehicle';
import ConversionFlowHeader from './ConversionFlowHeader';
import FlowRendererComponent from 'conversie-flow-bundle/assets/js/components/flow/renderers/FlowRendererComponent';

/* Conversion Fields */
import ConversionFieldChoice from './fields/ConversionFieldChoice';
import ConversionFieldChoiceSalesforce from './fields/ConversionFieldChoiceSalesforce';
import ConversionFieldHeader from './fields/ConversionFieldHeader';
import ConversionFieldChoiceSubscription from './fields/ConversionFieldChoiceSubscription';
import ConversionFieldDatePicker from './fields/ConversionFieldDatePicker';

/* Conversion Components */
import ConversionComponentHeader from './components/ConversionComponentHeader';
import ConversionComponentLeaseCalculator from './components/ConversionComponentLeaseCalculator';
import ConversionComponentFinanceCalculator from './components/ConversionComponentFinanceCalculator';
import ConversionComponentTradeproposalImage from './components/ConversionComponentTradeproposalImage';

/* Helpers */
import { getBanner } from './helpers';

/* Constants */
import { slideInOutTopToBottom } from '~/constants/animations'

/* Custom */
import { activatePrefillScript } from '~/custom/prefillFormFields';

export default function ConversionFlowForm({ form, flow, doRequest, setStep, handleClose, showRedirectLoader, inPage, isLoading }) {
    const Banner = getBanner(flow?.flowName);
    const activeStep = flow?.steps.find((step) => step.active);
    const lastStep = flow?.steps.find((step) => step.last);
    const [prevActiveStep, setPrevActiveStep] = useState(null);
    const gclId = sessionStorage.getItem('gclid') ? sessionStorage.getItem('gclid') : '';
    const utmSource = sessionStorage.getItem('utm_source') ? sessionStorage.getItem('utm_source') : '';
    const utmMedium = sessionStorage.getItem('utm_medium') ? sessionStorage.getItem('utm_medium') : '';

    if (flow && gclId !== '') {
        flow.gclid = gclId;
    }

    if (flow && utmSource !== '') {
        flow.utmSource = utmSource;
    }

    if (flow && utmMedium !== '') {
        flow.utmMedium = utmMedium;
    }

    useEffect(() => {
        if (flow && flow.steps) {
            const activeStep = flow.steps.find(step => step.active);
            if (activeStep && (!prevActiveStep || activeStep.position > prevActiveStep.position)) {
                const forms = Array.from(document.querySelectorAll('form'));
                activatePrefillScript(forms);
            }
            setPrevActiveStep(activeStep);
        }
    }, [flow?.steps]);

    return (
        <form noValidate onSubmit={form.handleSubmit(() => {
            if (activeStep?.position === ((lastStep?.position || 0) - 1) && flow.showLoaderBeforeRedirect) {
                showRedirectLoader();
            }
            doRequest({ action: 'submit' }, 'submitting');
        })} className={flow ? 'form--flow-' + flow.flowName : ''}>
            <ScrollAnchor key={activeStep?.position} />
            <ConversionFlowDataLayer activeStepId={flow?.steps?.find(step => step.active)?.id} inPage={inPage} flow={flow} />
            <FormProvider {...form}>
                <div className="row gutters-2 gutters-lg-4">
                    <AnimatePresence>
                        {flow?.steps?.filter((step) => !step?.hidden)?.length > 0 && (
                            <motion.div key={'steps'} {...slideInOutTopToBottom} className="col-12">
                                <ConversionFlowSteps
                                    steps={flow?.steps}
                                    setStep={setStep}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <AnimatePresence>
                        {Banner && (
                            <motion.div key={'banner'} {...slideInOutTopToBottom} className="col-12">
                                <Banner />
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <AnimatePresence exitBeforeEnter>
                        <motion.div key={activeStep?.id} {...slideInOutTopToBottom} className="col-12">
                            <div className="row gutters-8 justify-content-between">
                                <div className={`col-12 ${flow?.summary ? 'col-lg-6' : ''}`}>
                                    <div className="row gutters-2 gutters-lg-4">
                                        {(
                                            activeStep?.position > 1 &&
                                            !activeStep?.last &&
                                            flow?.flowName !== 'finance' &&
                                            flow?.flowName !== 'lease_enquire' &&
                                            flow?.flowName !== 'business_lease_enquire'
                                        ) && (
                                            <div className="col-12">
                                                <ConversionFlowPrevious
                                                    steps={flow?.steps}
                                                    setStep={setStep}
                                                />
                                            </div>
                                        )}
                                        {(flow?.form?.title || flow?.form?.intro) && (
                                            <div className="col-12">
                                                <ConversionFlowHeader
                                                    flow={flow}
                                                />
                                            </div>
                                        )}
                                        <FlowRendererComponent
                                            components={flow?.form?.components}
                                            path={`form.components`}
                                            id={`form-${flow?.flowId}.components`}
                                            renderComponent={(component, props) => {

                                                if (component.name === 'header') {
                                                    return (
                                                        <ConversionComponentHeader
                                                            {...props}
                                                        />
                                                    );
                                                }

                                                if (component.name === 'lease-calculator') {
                                                    return (
                                                        <ConversionComponentLeaseCalculator
                                                            {...props}
                                                        />
                                                    );
                                                }

                                                if (component.name === 'finance-calculator') {
                                                    return (
                                                        <ConversionComponentFinanceCalculator
                                                            {...props}
                                                        />
                                                    );
                                                }

                                                if (component.name === 'tradeproposal-image' || component.name === 'trade-in-vehicle' || component.name  === 'trade-in-image')  {
                                                    return (
                                                        <ConversionComponentTradeproposalImage
                                                            {...props}
                                                        />
                                                    );
                                                }
                                            }}
                                            renderField={(field, props) => {

                                                if (field.type === 'date') {
                                                    return (
                                                        <ConversionFieldDatePicker
                                                            {...props}
                                                        />
                                                    )
                                                }

                                                if (field.type === 'choice-radio' && field?.view === 'subscription') {
                                                    return (
                                                        <ConversionFieldChoiceSubscription
                                                            {...props}
                                                            formGroupClassName="form-group--flow-choice-grid"
                                                            customControlClassName="custom-control--expanded custom-radio--subscription w-100"
                                                            inputType="radio"
                                                        />
                                                    );
                                                }

                                                if (field.type === 'choice-radio' && field?.view === 'expanded') {
                                                    return (
                                                        <ConversionFieldChoice
                                                            {...props}
                                                            formGroupClassName="form-group--flow-choice-grid"
                                                            customControlClassName="custom-control--expanded w-100"
                                                            inputType="radio"
                                                        />
                                                    );
                                                }

                                                if (field.type === 'choice-checkbox' && field?.view === 'expanded') {
                                                    return (
                                                        <ConversionFieldChoice
                                                            {...props}
                                                            formGroupClassName="form-group--flow-choice-grid"
                                                            customControlClassName="custom-control--expanded w-100"
                                                            inputType="checkbox"
                                                        />
                                                    );
                                                }

                                                if (field.type === 'header') {
                                                    return (
                                                        <ConversionFieldHeader
                                                            {...props}
                                                        />
                                                    )
                                                }

                                                if (field.type === 'salesforce-consent') {
                                                    return (
                                                        <ConversionFieldChoiceSalesforce
                                                            {...props}
                                                            formGroupClassName="form-group--flow-choice-grid"
                                                            customControlClassName="custom-control w-100"
                                                            inputType="checkbox"
                                                        />
                                                    );
                                                }
                                            }}
                                        />
                                        {flow?.form?.buttonLabel && (
                                            <div className="col-12">
                                                <ConversionFlowSubmit
                                                    flow={flow}
                                                    isLoading={isLoading}
                                                    handleClose={handleClose}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {flow?.summary && (
                                    <div className="col-12 col-lg-5">
                                        <div className="row gutters-4 gutters-lg-6">
                                            <div className="col-12">
                                                <ConversionFlowVehicle
                                                    summary={flow?.summary}
                                                />
                                            </div>

                                            {flow?.summary?.categories && (
                                                <div className="col-12">
                                                    <ConversionFlowSummary
                                                        summary={flow?.summary}
                                                    />
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </FormProvider>
        </form>
    )
}
